import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, Settings, LogOut, Loader2, List, Code2, HelpCircle, Shield, MessageSquare, Star, Info, Eye } from 'lucide-react';


const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: var(--bg-dark);
  color: var(--text-main);
`;

const Sidebar = styled.aside`
  width: 280px;
  background: var(--bg-card);
  border-right: 1px solid var(--glass-border);
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  font-family: 'Outfit', sans-serif;
  font-size: 1.25rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 40px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--glass-border);
  
  span {
    background: linear-gradient(to right, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const NavMenu = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 500;
  color: ${props => props.$active ? 'var(--primary)' : 'var(--text-muted)'};
  background: ${props => props.$active ? 'rgba(99, 102, 241, 0.1)' : 'transparent'};
  transition: all 0.3s;
  
  &:hover {
    color: var(--primary);
    background: rgba(99, 102, 241, 0.05);
  }
`;

const LogoutBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  border: none;
  background: transparent;
  color: #ef4444;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
  font-size: 1rem;
  font-family: inherit;
  
  &:hover {
    background: rgba(239, 68, 68, 0.1);
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: 40px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const LoadingContainer = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary);
`;

const AdminLayout = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin/login');
                return;
            }

            try {
                const apiUrl = process.env.REACT_APP_API_URL || '';
                const response = await fetch(`${apiUrl}/api/auth/verify`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (response.ok) {
                    setIsAuthenticated(true);
                } else {
                    localStorage.removeItem('adminToken');
                    navigate('/admin/login');
                }
            } catch (err) {
                navigate('/admin/login');
            } finally {
                setLoading(false);
            }
        };

        checkAuth();
    }, [navigate]);

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
    };

    if (loading) {
        return (
            <LoadingContainer>
                <Loader2 className="spin" size={40} />
                <style>{`@keyframes spin { 100% { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
            </LoadingContainer>
        );
    }

    if (!isAuthenticated) return null;

    return (
        <LayoutContainer>
            <Sidebar>
                <SidebarHeader>
                    <Shield size={24} color="var(--primary)" />
                    <span>Admin CMS</span>
                </SidebarHeader>

                <NavMenu>
                    <NavItem to="/admin/dashboard" $active={location.pathname === '/admin/dashboard'}>
                        <LayoutDashboard size={20} /> Dashboard
                    </NavItem>
                    <NavItem to="/admin/projects" $active={location.pathname.startsWith('/admin/projects')}>
                        <FileText size={20} /> Client Projects
                    </NavItem>
                    <NavItem to="/admin/owned-projects" $active={location.pathname.startsWith('/admin/owned-projects')}>
                        <Star size={20} /> Ecosystem
                    </NavItem>
                    <NavItem to="/admin/services" $active={location.pathname.startsWith('/admin/services')}>
                        <List size={20} /> Services
                    </NavItem>
                    <NavItem to="/admin/faqs" $active={location.pathname.startsWith('/admin/faqs')}>
                        <HelpCircle size={20} /> FAQs
                    </NavItem>
                    <NavItem to="/admin/technologies" $active={location.pathname.startsWith('/admin/technologies')}>
                        <Code2 size={20} /> Technologies
                    </NavItem>
                    <NavItem to="/admin/reputation" $active={location.pathname.startsWith('/admin/reputation')}>
                        <MessageSquare size={20} /> Reputation
                    </NavItem>
                    <NavItem to="/admin/visitors" $active={location.pathname.startsWith("/admin/visitors")}>
                        <Eye size={20} /> Visitors
                    </NavItem>
                    <NavItem to="/admin/about" $active={location.pathname.startsWith('/admin/about')}>
                        <Info size={20} /> About Page
                    </NavItem>

                </NavMenu>

                <LogoutBtn onClick={handleLogout}>
                    <LogOut size={20} /> Logout
                </LogoutBtn>
            </Sidebar>

            <MainContent>
                <Outlet />
            </MainContent>
        </LayoutContainer>
    );
};

export default AdminLayout;
